import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  buildBrandTokens,
  BRAND_TOKEN_NAMES,
} from '../../utils/colorTokens';
import { useSubdomainBrand } from '../SubdomainBrandProvider';

/**
 * Applies the trainer / organization's brand colors to the app by
 * writing CSS custom properties to `document.documentElement`. Reads
 * `auth.user.branding` from Redux (populated by /api/auth on load)
 * and re-applies whenever the colors change — so the trainer's
 * settings page can preview their pick by dispatching a fresh
 * `loadUser` after save.
 *
 * Design notes:
 *  - Pure presentation. No authorization, no routing. Sits high in
 *    the tree so every route inherits the re-skin.
 *  - Semantic tokens (success / warning / error) are intentionally
 *    NOT overridden — green still means "success" for everyone.
 *  - Falls back to the stylesheet defaults when branding is absent
 *    or colors are invalid (the token utility silently skips bad
 *    hex strings).
 *  - Children render unchanged; this component returns them after
 *    the side-effect runs. Keeps the tree shallow.
 */
function ThemeProvider({ children }) {
  // Subscribe to the whole branding object once. Reads three colors
  // off it for the token build. Selecting the whole sub-object is
  // fine because it changes infrequently (only on loadUser + after
  // a brand-color save in settings) and lets us inspect the raw
  // shape in the temporary debug log below.
  const branding = useSelector((s) => s.auth?.user?.branding);
  // Before login (no resolved user branding), fall back to the
  // subdomain's brand so a coach's login/signup page is already in
  // their colors. Once authenticated, the user's own resolved branding
  // wins (it's authoritative — and for a client it's their coach's).
  const subdomain = useSubdomainBrand();
  const effective = branding?.colors ? branding : subdomain?.brand;
  const colors = effective?.colors;
  // Tolerate both shapes server-side normalization should already
  // collapse them — but in case it didn't (API didn't restart after
  // the normalizer was added, or some other path bypassed the
  // resolver), we coerce arrays here too. This is the same logic
  // that runs server-side in Trainer.getBranding's normalizeColors.
  let primary;
  let secondary;
  let accent;
  if (Array.isArray(colors)) {
    [primary, secondary, accent] = colors;
  } else if (colors && typeof colors === 'object') {
    primary = colors.primary;
    secondary = colors.secondary;
    accent = colors.accent;
  }

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const tokens = buildBrandTokens({ primary, secondary, accent });
    Object.entries(tokens).forEach(([name, value]) => {
      root.style.setProperty(name, value);
    });
    return () => {
      // Strip our overrides on unmount so the stylesheet's :root
      // defaults reapply (e.g. when the user signs out and the
      // ThemeProvider rerenders with an empty branding object).
      BRAND_TOKEN_NAMES.forEach((name) => {
        root.style.removeProperty(name);
      });
    };
  }, [primary, secondary, accent]);

  return children;
}

export default ThemeProvider;
